import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";

// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = "/api";

// ── Renders Quill Delta JSON (contentJson) as React <p> elements ───────────
const renderQuillContent = (contentJson) => {
  try {
    const parsed = JSON.parse(contentJson);
    const text = parsed.ops
      .map((op) => op.insert)
      .join("")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    return text
      .split("\n\n")
      .filter(Boolean)
      .map((para, i) => (
        <p key={i} className="article-paragraph">{para}</p>
      ));
  } catch {
    return <p>Content unavailable.</p>;
  }
};

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric",
  });
};

const formatTime = (t) => {
  if (!t) return "";
  const [hours, minutes] = t.split(":");
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? "PM" : "AM";
  return `${hour % 12 || 12}:${minutes} ${ampm}`;
};

const MinistryArticle = () => {
  const { slug } = useParams();

  const [article, setArticle]   = useState(null);
  const [related, setRelated]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // ── Fetch article detail + related ────────────────────────────────────────
  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/ministry-articles/${slug}/`, {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        });
        if (!response.ok) throw new Error(`Failed to fetch article: ${response.status}`);
        const data = await response.json();
        setArticle(data);

        // Fetch related articles in same category
        if (data.category) {
          const relRes = await fetch(
            `${API_BASE}/ministry-articles/?category=${data.category}&page_size=4`,
            { headers: { "ngrok-skip-browser-warning": "69420" } }
          );
          if (relRes.ok) {
            const relData = await relRes.json();
            const results = Array.isArray(relData) ? relData : (relData.results ?? []);
            setRelated(results.filter((a) => a.slug !== slug).slice(0, 3));
          }
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <div style={{ width: 44, height: 44, border: "4px solid #e8f4fd", borderTopColor: "#0b1c6b", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </>
  );

  // ── Error ────────────────────────────────────────────────────────────────
  if (error || !article) return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <div style={{ textAlign: "center", padding: "80px 20px" }}>
        <div style={{ fontSize: "3.5rem", marginBottom: 16 }}>⚠️</div>
        <h4 style={{ color: "#0b1c6b" }}>Could not load article</h4>
        <p style={{ color: "#6b7280" }}>{error || "Article not found."}</p>
        <Link to="/ministry" className="btn btn-outline-primary rounded-pill px-4 mt-3">
          ← Back to Ministry
        </Link>
      </div>
    </>
  );

  const gallery = article.gallery ?? [];
  const tags    = article.tags ?? [];

  return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css" />

      <div className="article-page">

        {/* BREADCRUMB */}
        <section className="bg-light py-3">
          <div className="container">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/" className="text-decoration-none">
                    <i className="bi bi-house-door me-1"></i>Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/ministry" className="text-decoration-none">Ministry</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {article.category_display || "Article"}
                </li>
              </ol>
            </nav>
          </div>
        </section>

        {/* HERO IMAGE */}
        <section className="article-hero">
          {article.cover_image ? (
            <img src={article.cover_image} alt={article.title} className="w-100" />
          ) : (
            <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#0b1c6b,#159EEC)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "6rem" }}>
              ✝️
            </div>
          )}
          <div className="hero-gradient"></div>
        </section>

        {/* ARTICLE HEADER */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10 col-xl-8">

                {/* Category badge */}
                {article.category_display && (
                  <div className="mb-3">
                    <span className="badge bg-info bg-opacity-10 text-info text-uppercase px-3 py-2">
                      <i className="bi bi-bookmark-fill me-2"></i>
                      {article.category_display}
                    </span>
                  </div>
                )}

                {/* Title */}
                <h1 className="display-4 fw-bold text-primary mb-4">{article.title}</h1>

                {/* Meta */}
                <div className="d-flex flex-wrap align-items-center gap-4 mb-4 pb-4 border-bottom">
                  {article.author_name && (
                    <div className="d-flex align-items-center text-muted">
                      <i className="bi bi-person-circle fs-5 me-2"></i>
                      <span>{article.author_name}</span>
                    </div>
                  )}
                  {article.article_date && (
                    <div className="d-flex align-items-center text-muted">
                      <i className="bi bi-calendar3 fs-5 me-2"></i>
                      <span>{formatDate(article.article_date)}</span>
                    </div>
                  )}
                  {article.article_time && (
                    <div className="d-flex align-items-center text-muted">
                      <i className="bi bi-clock fs-5 me-2"></i>
                      <span>{formatTime(article.article_time)}</span>
                    </div>
                  )}
                  <div className="d-flex align-items-center text-muted">
                    <i className="bi bi-eye fs-5 me-2"></i>
                    <span>5 min read</span>
                  </div>
                </div>

                {/* Share buttons */}
                <div className="d-flex align-items-center gap-3 mb-5">
                  <span className="fw-bold text-muted me-2">Share:</span>
                  <button className="btn btn-outline-primary btn-sm rounded-circle share-btn">
                    <i className="bi bi-facebook"></i>
                  </button>
                  <button className="btn btn-outline-primary btn-sm rounded-circle share-btn">
                    <i className="bi bi-twitter-x"></i>
                  </button>
                  <button className="btn btn-outline-primary btn-sm rounded-circle share-btn">
                    <i className="bi bi-whatsapp"></i>
                  </button>
                  <button className="btn btn-outline-primary btn-sm rounded-circle share-btn">
                    <i className="bi bi-linkedin"></i>
                  </button>
                  <button className="btn btn-outline-primary btn-sm rounded-circle share-btn">
                    <i className="bi bi-link-45deg"></i>
                  </button>
                </div>

                {/* Article body — rendered from Quill Delta */}
                <div className="article-content">
                  {renderQuillContent(article.contentJson)}
                </div>

                {/* Tags */}
                {tags.length > 0 && (
                  <div className="mt-5 pt-4 border-top">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <span className="fw-bold text-muted me-2">
                        <i className="bi bi-tags me-1"></i>Tags:
                      </span>
                      {tags.map((tag) => (
                        <span key={tag.id} className="badge bg-light text-dark border px-3 py-2">
                          {tag.title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </section>

        {/* PHOTO GALLERY — only when images exist */}
        {gallery.length > 0 && (
          <section className="py-5 bg-light">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-10 col-xl-8">
                  <h3 className="fw-bold text-primary mb-4">
                    <i className="bi bi-images me-2"></i>Photo Gallery
                  </h3>
                  <div className="row g-3">
                    {gallery
                      .sort((a, b) => a.order - b.order)
                      .map((item) => (
                        <div className="col-6 col-md-3" key={item.id}>
                          <div className="gallery-item">
                            <img
                              src={item.image}
                              alt={item.caption || `Gallery image`}
                              className="img-fluid rounded-3 shadow-sm w-100"
                              title={item.caption || ""}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* RELATED ARTICLES — only when results exist */}
        {related.length > 0 && (
          <section className="py-5 bg-white">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-lg-10 col-xl-8">
                  <h3 className="fw-bold text-primary mb-4">
                    <i className="bi bi-journal-text me-2"></i>Related Articles
                  </h3>
                  <div className="row g-4">
                    {related.map((rel) => (
                      <div className="col-md-4" key={rel.id}>
                        <Link to={`/ministry/${rel.slug}`} className="text-decoration-none">
                          <div className="card border-0 shadow-sm rounded-4 h-100 related-card">
                            {rel.cover_image ? (
                              <img
                                src={rel.cover_image}
                                className="card-img-top"
                                alt={rel.title}
                                style={{ height: "180px", objectFit: "cover" }}
                              />
                            ) : (
                              <div style={{ height: 180, background: "linear-gradient(135deg,#0b1c6b,#159EEC)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "3rem" }}>
                                ✝️
                              </div>
                            )}
                            <div className="card-body">
                              {rel.category_display && (
                                <span className="badge bg-info bg-opacity-10 text-info text-uppercase mb-2" style={{ fontSize: "10px" }}>
                                  {rel.category_display}
                                </span>
                              )}
                              <h6 className="card-title fw-bold text-primary mb-2">{rel.title}</h6>
                              <p className="card-text text-muted mb-0" style={{ fontSize: "14px" }}>
                                <i className="bi bi-calendar3 me-1"></i>
                                {formatDate(rel.article_date)}
                              </p>
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* BACK TO ARTICLES */}
        <section className="py-4 bg-light">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10 col-xl-8">
                <Link to="/ministry" className="btn btn-outline-primary rounded-pill px-4">
                  <i className="bi bi-arrow-left me-2"></i>Back to All Articles
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* SOCIAL */}
        <section className="social-section py-5 text-center text-white">
          <div className="container py-4">
            <h2 className="display-5 fw-bold mb-3">Follow Us</h2>
            <p className="fs-6 mb-4 mx-auto" style={{ maxWidth: "600px" }}>
              Stay connected with Gospel City Church International through our social platforms.
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <a href="https://www.linkedin.com/in/gospel-city-church-international-650a562a0/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-linkedin fs-4"></i>
              </a>
              <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
            <i className="bi bi-chat-dots fs-4"></i>
            </a>
              <a href="https://x.com/GospelCityChur?t=x8dsFbFkghmYh02lV0FVxQ&s=03" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-twitter-x fs-4"></i>
              </a>
              <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-whatsapp fs-4"></i>
              </a>
              <a href="https://youtube.com/@gospelcitychurchinternational?si=1pZx2ak4ss3b4HuU" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-youtube fs-4"></i>
              </a>
            </div>
          </div>
        </section>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');
          * { font-family: 'Lexend', sans-serif; }

          .article-hero { position: relative; height: 500px; overflow: hidden; background: #000; }
          .article-hero img { height: 100%; object-fit: cover; opacity: 0.9; }
          .hero-gradient { position: absolute; bottom: 0; left: 0; right: 0; height: 150px; background: linear-gradient(to bottom, transparent, rgba(255,255,255,1)); }

          .breadcrumb-item a { color: #0b1c6b; }
          .breadcrumb-item a:hover { color: #159EEC; }
          .breadcrumb-item.active { color: #6c757d; }

          .article-content { font-size: 18px; line-height: 1.8; color: #333; }
          .article-paragraph { margin-bottom: 1.5rem; }
          .article-paragraph:first-child { font-size: 1.2rem; font-weight: 500; color: #555; margin-bottom: 2rem; }

          .share-btn { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; padding: 0; transition: all 0.3s; }
          .share-btn:hover { background: #0b1c6b; border-color: #0b1c6b; color: #fff; transform: translateY(-3px); }

          .gallery-item { overflow: hidden; border-radius: 12px; transition: transform 0.3s; }
          .gallery-item:hover { transform: scale(1.05); }
          .gallery-item img { transition: transform 0.3s; cursor: pointer; height: 200px; object-fit: cover; }
          .gallery-item:hover img { transform: scale(1.1); }

          .related-card { transition: transform 0.3s, box-shadow 0.3s; }
          .related-card:hover { transform: translateY(-5px); box-shadow: 0 15px 40px rgba(26,58,161,0.15) !important; }

          .text-primary { color: #1a3aa1 !important; }
          .text-info { color: #159EEC !important; }
          .bg-info { background-color: #159EEC !important; }
          .btn-primary { background: #0b1c6b; border-color: #0b1c6b; }
          .btn-primary:hover { background: #159EEC; border-color: #159EEC; }
          .btn-outline-primary { color: #0b1c6b; border-color: #0b1c6b; }
          .btn-outline-primary:hover { background: #0b1c6b; border-color: #0b1c6b; color: #fff; }
          .social-section { background: linear-gradient(135deg,#0b1c6b,#159EEC); }
          .social-icon { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; transition: all 0.3s; }
          .social-icon:hover { background: rgba(255,255,255,0.3); transform: translateY(-4px) scale(1.1); }

          @media (max-width: 768px) {
            .article-hero { height: 300px; }
            .article-content { font-size: 16px; }
            .display-4 { font-size: 2rem; }
            .gallery-item img { height: 150px; }
          }
        `}</style>
      </div>
    </>
  );
};

export default MinistryArticle;